import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Eye, EyeOff, PlayCircle, ShieldCheck, Users2, GraduationCap, Sparkles,
  TrendingUp, BookOpen, MessageSquare, Trophy,
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { LogoIcon } from "@/components/Logo";
import VideoIntroModal from "@/components/VideoIntroModal";

const DEMO_MODE = String(process.env.REACT_APP_DEMO_MODE || "false").toLowerCase() === "true";
const DEMO = [
  { role: "Öğretmen", email: "ogretmen@edupilot.ai", pw: "ogretmen123", tint: "indigo", icon: BookOpen },
  { role: "Öğrenci", email: "ogrenci@edupilot.ai", pw: "ogrenci123", tint: "cyan", icon: GraduationCap },
  { role: "Veli", email: "veli@edupilot.ai", pw: "veli123", tint: "orange", icon: Users2 },
  { role: "Yönetici", email: "admin@edupilot.ai", pw: "admin123", tint: "emerald", icon: ShieldCheck },
];

export default function Login() {
  const { login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [otp, setOtp] = useState("");
  const [need2fa, setNeed2fa] = useState(false);
  const [lockedMsg, setLockedMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [videoOpen, setVideoOpen] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    setLockedMsg("");
    setErrorMsg("");
    try {
      await login(email, pw, need2fa ? otp : null);
      toast.success("Hoş geldin!");
      nav("/panel");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 206 || (typeof detail === "object" && detail?.require_2fa)) {
        setNeed2fa(true);
        toast.message("2FA kodunuzu girin");
        return;
      }
      if (status === 429) {
        setLockedMsg(typeof detail === "string" ? detail : "Çok fazla başarısız deneme. Bir süre sonra tekrar deneyin.");
        return;
      }
      setErrorMsg("E-posta veya şifre hatalı. Lütfen tekrar deneyin.");
    }
  };

  const quick = async (d) => {
    setEmail(d.email); setPw(d.pw);
    try {
      await login(d.email, d.pw);
      toast.success(`${d.role} olarak giriş yapıldı`);
      nav("/panel");
    } catch { toast.error("Demo giriş başarısız"); }
  };

  return (
    <div className="min-h-screen auth-bg grid lg:grid-cols-[minmax(0,1fr)_min(560px,100%)]">
      {/* ==================== HERO PANEL ==================== */}
      <main role="main" className="hidden lg:flex flex-col justify-between p-10 xl:p-16 overflow-hidden relative">
        <Link to="/" className="flex items-center gap-2.5 z-10" aria-label="Leranio ana sayfa">
          <motion.span initial={{ rotate: -12, scale: 0.7, opacity: 0 }} animate={{ rotate: 0, scale: 1, opacity: 1 }} transition={{ type: "spring", stiffness: 200, damping: 15 }} className="inline-flex"><LogoIcon size={40} /></motion.span>
          <motion.span initial={{ x: -10, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.15, duration: 0.5 }} className="font-display font-bold text-xl">Leranio</motion.span>
        </Link>

        <div className="max-w-xl space-y-6 z-10">
          <Badge className="bg-indigo-500/10 text-indigo-600 border-indigo-500/25" data-testid="hero-badge">
            <Sparkles size={12} className="mr-1" aria-hidden="true" /> Türkiye için özel · KVKK uyumlu
          </Badge>
          <h1 className="font-display text-4xl xl:text-5xl font-bold leading-[1.1] tracking-tight" data-testid="hero-title">
            Okul yönetimi ve öğrenme,<br />
            <span className="brand-text-gradient">yapay zekâyla daha akıllı</span>.
          </h1>
          <p className="text-muted-foreground text-base leading-relaxed max-w-md">
            Öğretmen, öğrenci ve veli için tek panelde. AI Sokratik Öğretmen, adaptif meydan okumalar,
            konu boşluğu tespiti ve otomatik veli iletişimi.
          </p>

          <div className="flex flex-wrap items-center gap-3 pt-2" role="group" aria-label="Tur ve kayıt eylemleri">
            <Button
              onClick={() => setVideoOpen(true)}
              data-testid="watch-intro-btn"
              variant="outline"
              className="gap-2 h-11 px-5 rounded-xl border-2 hover:bg-foreground/5"
            >
              <PlayCircle size={18} aria-hidden="true" />
              Ürünü 60 saniyede keşfet
            </Button>
            <Link to="/kayit" className="text-sm text-muted-foreground hover:text-foreground hover:underline" data-testid="hero-register-link">
              veya hesap oluştur →
            </Link>
          </div>

          {/* Trust signals — verifiable only */}
          <ul className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-4 text-xs text-muted-foreground" aria-label="Güven işaretleri">
            <li className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-emerald-500" aria-hidden="true" /> KVKK Kontrolleri</li>
            <li className="flex items-center gap-1.5"><Users2 size={14} className="text-indigo-500" aria-hidden="true" /> Rol-Tabanlı Erişim</li>
            <li className="flex items-center gap-1.5"><MessageSquare size={14} className="text-cyan-500" aria-hidden="true" /> Türkçe Destek</li>
          </ul>
        </div>

        {/* ==================== REAL-UI MONTAGE ==================== */}
        <div className="relative h-72 mt-4 hidden xl:block" aria-hidden="true" data-testid="ui-montage">
          {/* Card 1: KpiCard mockup */}
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.3, duration: 0.7 }}
            className="absolute left-0 top-2 w-64 p-4 rounded-2xl border border-border bg-card shadow-lg backdrop-blur-sm"
          >
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Ödevlerim</div>
                <div className="font-display text-3xl font-bold mt-1.5">12</div>
                <div className="text-xs text-muted-foreground mt-1">8 teslim edildi</div>
              </div>
              <div className="w-10 h-10 rounded-xl bg-cyan-500/15 grid place-items-center text-cyan-500"><BookOpen size={18} /></div>
            </div>
            <div className="mt-3 h-1.5 rounded-full bg-secondary overflow-hidden">
              <div className="h-full bg-cyan-500" style={{ width: "67%" }} />
            </div>
          </motion.div>

          {/* Card 2: AI Teacher chat bubble */}
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.5, duration: 0.7 }}
            className="absolute right-4 top-0 w-72 p-4 rounded-2xl border border-border bg-card shadow-lg backdrop-blur-sm"
          >
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 grid place-items-center text-white"><Sparkles size={14} /></div>
              <div>
                <div className="text-xs font-semibold">AI Öğretmen</div>
                <div className="text-[10px] text-emerald-500">Aktif</div>
              </div>
            </div>
            <div className="text-xs bg-indigo-500/[0.08] text-foreground p-2.5 rounded-xl rounded-tl-sm leading-relaxed">
              Sorunu adım adım çözelim: <em>2x² − 8x + 6 = 0</em> denkleminde ilk olarak hangi kuralı kullanmalıyız?
            </div>
            <div className="text-[10px] text-muted-foreground italic mt-1.5">Sokratik yöntem · sana cevabı vermez, keşfettirir.</div>
          </motion.div>

          {/* Card 3: Parent report snippet */}
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.7, duration: 0.7 }}
            className="absolute left-16 bottom-0 w-72 p-4 rounded-2xl border border-border bg-card shadow-lg backdrop-blur-sm"
          >
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-lg bg-orange-500/15 text-orange-500 grid place-items-center"><TrendingUp size={14} /></div>
              <div className="text-xs font-semibold">Zeynep · Haftalık Rapor</div>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div><div className="text-lg font-display font-bold text-emerald-500">92</div><div className="text-[9px] uppercase text-muted-foreground">Not Ort.</div></div>
              <div><div className="text-lg font-display font-bold text-indigo-500">4</div><div className="text-[9px] uppercase text-muted-foreground">Ödev</div></div>
              <div><div className="text-lg font-display font-bold text-orange-500">+180</div><div className="text-[9px] uppercase text-muted-foreground">XP</div></div>
            </div>
          </motion.div>

          {/* Small trophy accent */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ delay: 0.9, type: "spring" }}
            className="absolute right-24 bottom-14 w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-400 to-rose-500 shadow-lg grid place-items-center text-white"
          >
            <Trophy size={22} />
          </motion.div>
        </div>

        <footer className="text-xs text-muted-foreground flex items-center gap-4 z-10">
          <span>© {new Date().getFullYear()} Leranio</span>
          <Link to="/gizlilik" className="hover:text-foreground hover:underline" data-testid="footer-privacy">Gizlilik</Link>
          <Link to="/sartlar" className="hover:text-foreground hover:underline" data-testid="footer-terms">Kullanım Koşulları</Link>
        </footer>
      </main>

      {/* ==================== LOGIN FORM CARD ==================== */}
      <div className="flex items-center justify-center p-6 md:p-10">
        <Card className="w-full max-w-md p-8 md:p-10 ambient-shadow border-border" role="region" aria-labelledby="login-heading">
          <div className="lg:hidden mb-6 flex items-center gap-2.5">
            <LogoIcon size={40} />
            <span className="font-display font-bold text-xl">Leranio</span>
          </div>
          <h2 id="login-heading" className="font-display text-2xl font-bold mb-1">Tekrar hoş geldin</h2>
          <p className="text-sm text-muted-foreground mb-6">Devam etmek için giriş yap.</p>

          {lockedMsg && (
            <div role="alert" aria-live="assertive" className="mb-4 p-3 rounded-xl border border-red-500/40 bg-red-500/10 text-sm text-red-700 dark:text-red-400" data-testid="login-locked-msg">
              {lockedMsg}
            </div>
          )}
          {errorMsg && (
            <div role="alert" aria-live="assertive" className="mb-4 p-3 rounded-xl border border-red-500/40 bg-red-500/10 text-sm text-red-700 dark:text-red-400" data-testid="login-error-msg">
              {errorMsg}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4" data-testid="login-form" noValidate>
            <div className="space-y-1.5">
              <Label htmlFor="email">E-posta</Label>
              <Input id="email" type="email" autoComplete="email" placeholder="ornek@okul.edu" value={email} onChange={(e) => setEmail(e.target.value)} required aria-invalid={!!errorMsg} aria-describedby={errorMsg ? "login-error-msg" : undefined} data-testid="login-email-input" />
            </div>
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <Label htmlFor="pw">Şifre</Label>
                <Link to="/sifremi-unuttum" className="text-xs text-muted-foreground hover:text-foreground hover:underline" data-testid="forgot-password-link">Şifremi unuttum</Link>
              </div>
              <div className="relative">
                <Input id="pw" type={show ? "text" : "password"} autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} required aria-invalid={!!errorMsg} data-testid="login-password-input" className="pr-10" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground" aria-label={show ? "Şifreyi gizle" : "Şifreyi göster"}>
                  {show ? <EyeOff size={16} aria-hidden="true" /> : <Eye size={16} aria-hidden="true" />}
                </button>
              </div>
            </div>
            {need2fa && (
              <div className="space-y-1.5">
                <Label htmlFor="otp">6 haneli doğrulama kodu</Label>
                <Input id="otp" inputMode="numeric" autoComplete="one-time-code" maxLength={6} placeholder="123456" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} data-testid="login-otp-input" />
              </div>
            )}
            <Button type="submit" className="w-full h-11 rounded-xl" disabled={loading} data-testid="login-submit-btn">
              {loading ? "Giriş yapılıyor..." : need2fa ? "Doğrula ve Gir" : "Giriş Yap"}
            </Button>
          </form>

          {DEMO_MODE && (
            <>
              <div className="my-6 flex items-center gap-3 text-xs text-muted-foreground" data-testid="demo-divider">
                <span className="flex-1 h-px bg-border" />hızlı demo hesabıyla dene<span className="flex-1 h-px bg-border" />
              </div>
              <div className="grid grid-cols-2 gap-2" data-testid="demo-cards" role="group" aria-label="Demo hesaplar">
                {DEMO.map((d) => {
                  const Icon = d.icon;
                  return (
                    <button
                      key={d.email}
                      onClick={() => quick(d)}
                      data-testid={`demo-${d.role.toLowerCase()}`}
                      className={`p-3 rounded-xl border border-border hover:border-${d.tint}-400 hover:bg-${d.tint}-500/5 transition-colors text-left flex items-start gap-2`}
                      aria-label={`${d.role} demosuna gir`}
                    >
                      <div className={`w-7 h-7 rounded-lg bg-${d.tint}-500/10 text-${d.tint}-600 grid place-items-center shrink-0`}>
                        <Icon size={14} aria-hidden="true" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className={`text-xs font-semibold text-${d.tint}-600`}>{d.role} demosuna gir</div>
                        <div className="text-[10px] text-muted-foreground truncate" title={d.email}>{d.email}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </>
          )}

          <div className="mt-6 text-center text-sm text-muted-foreground">
            Hesabın yok mu? <Link to="/kayit" className="text-foreground font-medium hover:underline" data-testid="go-to-register">Kayıt ol</Link>
          </div>
          <div className="mt-4 text-center text-[11px] text-muted-foreground flex items-center justify-center gap-3 lg:hidden">
            <Link to="/gizlilik" className="hover:underline">Gizlilik</Link>
            <span>·</span>
            <Link to="/sartlar" className="hover:underline">Koşullar</Link>
          </div>
        </Card>
      </div>

      <VideoIntroModal open={videoOpen} onOpenChange={setVideoOpen} />
    </div>
  );
}
